import { useMutation, useQuery } from '@apollo/client';
import { SelectValue } from 'elements/minute/sidebar/sidebarComponents/atomic/SelectValue';
import {
    AddProgramItemToCategory,
    AddProgramItemToCategoryVariables
} from 'graphql/mutation/__generated__/AddProgramItemToCategory';
import {
    AssignRoomToGuideProgram,
    AssignRoomToGuideProgramVariables
} from 'graphql/mutation/__generated__/AssignRoomToGuideProgram';
import {
    CreateProgramItem,
    CreateProgramItemVariables
} from 'graphql/mutation/__generated__/createProgramItem';
import {
    LinkStoryToProgramItem,
    LinkStoryToProgramItemVariables
} from 'graphql/mutation/__generated__/linkStoryToProgramItem';
import {
    G_ADD_PROGRAM_ITEM_TO_CATEGORY,
    G_ASSIGN_ROOM_TO_PROGRAM,
    G_CREATE_PROGRAM_ITEM,
    G_DELETE_PROGRAM_ITEM,
    G_LINK_STORY_TO_PROGRAM
} from 'graphql/mutation/guideMutation';
import {
    GuideQueryView,
    GuideQueryViewVariables
} from 'graphql/query/__generated__/GuideQueryView';
import { GuidesForCurrentUserQuery } from 'graphql/query/__generated__/GuidesForCurrentUserQuery';
import { CURRENT_USER_GUIDES_QUERY, GUIDE_QUERY_VIEW } from 'graphql/query/guideQuery';
import { EToastType } from 'libs/types/site/IClient';
import { userMessage } from 'libs/utils/logic/ui/uiLogic';
import { S_PageStyle } from 'pages/page/PageStyle';
import { convertTimeAndDate } from 'pages/special/UpdateSeafoodProgram/convertSeafoodTime';
import {
    SeafoodCategory1Id,
    SeafoodCategory1Urls,
    SeafoodCategory2Id,
    SeafoodCategory2Urls,
    SeafoodCategory3Id,
    SeafoodCategory3Urls
} from 'pages/special/UpdateSeafoodProgram/seafoodCategories';
import {
    SeafoodMarkersMap,
    seafoodProgramSource
} from 'pages/special/UpdateSeafoodProgram/seafoodProgramSource';
import { useRef, useState } from 'react';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const UpdateSeafoodProgram = (props: Props): JSX.Element => {
    //RUN BEFORE: Create details
    //load the guide with details and programme
    //delete all items in the programme
    //Create new programme items -> Assign the story based on sourceURL -> Update the title and description from the story

    const [createProgramMutation] = useMutation<CreateProgramItem, CreateProgramItemVariables>(
        G_CREATE_PROGRAM_ITEM
    );

    const [linkDetailToProgram] = useMutation<
        LinkStoryToProgramItem,
        LinkStoryToProgramItemVariables
    >(G_LINK_STORY_TO_PROGRAM);

    const [assignRoomMutation] = useMutation<
        AssignRoomToGuideProgram,
        AssignRoomToGuideProgramVariables
    >(G_ASSIGN_ROOM_TO_PROGRAM);

    const [guideId, setGuideId] = useState<string>('');
    const created = useRef(0);

    const allGuides = useQuery<GuidesForCurrentUserQuery>(CURRENT_USER_GUIDES_QUERY);

    const [deleteProgram] = useMutation(G_DELETE_PROGRAM_ITEM, {
        onCompleted: () => {
            guideQuery.refetch();
            userMessage(EToastType.SUCCESS, 'program gone');
        }
    });

    const guideQuery = useQuery<GuideQueryView, GuideQueryViewVariables>(GUIDE_QUERY_VIEW, {
        variables: {
            guideIdOrPath: guideId
        },
        nextFetchPolicy: 'network-only',
        skip: !guideId
    });

    const [addToCategoryMutation] = useMutation<
        AddProgramItemToCategory,
        AddProgramItemToCategoryVariables
    >(G_ADD_PROGRAM_ITEM_TO_CATEGORY);

    function deleteAllProgram() {
        guideQuery.data?.guideView.programItems.forEach((program) => {
            deleteProgram({
                variables: {
                    guideProgramItemId: program.guideProgramItemId,
                    guideId
                }
            });
        });
    }

    async function createProgram() {
        created.current = 0;
        for (let i = 0; i < seafoodProgramSource.length; i++) {
            const e = seafoodProgramSource[i];
            const matchingDetail = guideQuery.data?.guideView.stories.find(
                (d) => d.importKey === e.sourceUrl
            );
            console.log('Matching detail');
            const times = convertTimeAndDate(e.time, e.date);
            console.log('Times', times);
            const resp = await createProgramMutation({
                variables: {
                    guideId,
                    input: {
                        date: times.date,
                        timeStart: times.startDateString,
                        timeEnd: times.endDateString,
                        loadDetailFromStoryId: true
                    }
                }
            });

            const guideProgramItemId =
                resp.data?.g_createProgramItem?.programItem?.guideProgramItemId;

            if (matchingDetail?.storyId) {
                await linkDetailToProgram({
                    variables: {
                        guideId,
                        guideProgramItemId,
                        storyId: matchingDetail?.storyId
                    }
                });
            }

            const markerId = SeafoodMarkersMap[e.place] ?? null;

            if (markerId) {
                await assignRoomMutation({
                    variables: {
                        guideId,
                        guideProgramItemId,
                        worldMapMarkerId: markerId
                    }
                });
            }

            const categoryIds = [];

            if (SeafoodCategory1Urls.includes(e.sourceUrl)) {
                categoryIds.push(SeafoodCategory1Id);
            }
            if (SeafoodCategory2Urls.includes(e.sourceUrl)) {
                categoryIds.push(SeafoodCategory2Id);
            }
            if (SeafoodCategory3Urls.includes(e.sourceUrl)) {
                categoryIds.push(SeafoodCategory3Id);
            }
            for (const categoryId of categoryIds) {
                await addToCategoryMutation({
                    variables: {
                        guideId,
                        guideProgramItemId,
                        worldMapSetId: categoryId
                    }
                });
            }

            created.current++;
        }
    }

    function findMatch(sourceUrl: string) {
        return guideQuery.data?.guideView.stories.find((d) => d.importKey === sourceUrl);
    }

    const guideView = guideQuery.data?.guideView;

    return (
        <S_UpdateSeafoodProgram className={props.className}>
            <S_Label>
                <S_LabelText>Guide:</S_LabelText>
                <S_SelectValue
                    options={allGuides.data?.currentUserGuides?.map((e) => ({
                        label: e.title,
                        value: e.guideId
                    }))}
                    onChange={(e) => {
                        setGuideId(e.value);
                    }}
                />
            </S_Label>
            {guideView && (
                <>
                    {guideView.programItems?.length > 0 && (
                        <button onClick={deleteAllProgram}>
                            Delete all program items: {guideView.programItems?.length}
                        </button>
                    )}
                    <span>
                        {created.current}/{seafoodProgramSource.length}
                    </span>
                    <button onClick={createProgram}>Create program</button>
                    <table>
                        {seafoodProgramSource.map((e) => {
                            return (
                                <S_Tr key={e.sourceUrl}>
                                    <td>{e.title}</td>
                                    <td>{findMatch(e.sourceUrl)?.importKey}</td>
                                </S_Tr>
                            );
                        })}
                    </table>
                </>
            )}
        </S_UpdateSeafoodProgram>
    );
};

const S_UpdateSeafoodProgram = styled(S_PageStyle)`
    padding: 30px;
    padding-right: 550px;
`;

const S_Label = styled.label`
    display: flex;
    flex-direction: row;
    gap: 50px;
    margin-bottom: 20px;
`;

const S_LabelText = styled.div`
    width: 100px;
`;

const S_SelectValue = styled(SelectValue)`
    width: 200px;
`;

const S_Tr = styled.tr`
    outline: 1px solid black;
`;
